import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson, ApiError } from '../../lib/api'
import { dateTimeText } from './api'
import styles from './GodMode.module.css'

type Counts = { create: number; already: number; skipped: number; held: number }
type Preview = {
  packhouse: { packhouse_id: number; name: string }
  extracted_at: string | null
  counts: Record<string, Counts>
  to_create: number
  held: { section: string; name: string; path: string; reason: string }[]
  held_count: number
  skipped: { section: string; reason: string; count: number }[]
  warnings: string[]
  plan_hash: string
}
type Run = {
  id: string
  packhouse_id: number
  extracted_at: string | null
  created_at: string
  created_by: string | null
  result: { counts: Record<string, Counts>; held_count: number }
}

const SECTIONS: [string, string][] = [
  ['sites', 'Sites (orchards)'],
  ['blocks', 'Blocks'],
  ['subs', 'Sub-blocks'],
  ['rows', 'Rows'],
  ['varieties', 'Varieties'],
  ['jobs', 'Picking jobs'],
  ['links', 'Rows in jobs'],
  ['groups', 'Groups (contractors)'],
  ['teams', 'Teams'],
  ['tags', 'Tags'],
]
const sectionName = (k: string) => SECTIONS.find((s) => s[0] === k)?.[1] || k

// God Mode's import from Binwise: an extract of the client's legacy
// packhouse (scripts/binwise-extract.mjs) is previewed, record by record,
// then imported. Importing again later adds only what is new.
export function LegacyImportCard({ clientId, onImported }: { clientId: string; onImported: () => void }) {
  const { user } = useAuth()
  const [runs, setRuns] = useState<Run[]>([])
  const [extract, setExtract] = useState<unknown>(null)
  const [fileName, setFileName] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [runKey, setRunKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const loadRuns = useCallback(async () => {
    try {
      setRuns((await apiJson<{ runs: Run[] }>(user, `/api/god-mode/clients/${clientId}/legacy-import`)).runs)
    } catch {
      setRuns([])
    }
  }, [user, clientId])

  useEffect(() => {
    loadRuns()
  }, [loadRuns])

  const look = async (x: unknown) => {
    setBusy(true)
    setError(null)
    setPreview(null)
    try {
      const r = await apiJson<{ preview: Preview }>(user, `/api/god-mode/clients/${clientId}/legacy-import/preview`, { method: 'POST', body: { extract: x } })
      setPreview(r.preview)
      setRunKey(crypto.randomUUID())
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const choose = async (file: File | undefined) => {
    setDone(null)
    setPreview(null)
    setError(null)
    if (!file) return
    setFileName(file.name)
    let x: unknown
    try {
      x = JSON.parse(await file.text())
    } catch {
      setExtract(null)
      setError('This file is not JSON. Choose the file binwise-extract.mjs wrote.')
      return
    }
    setExtract(x)
    await look(x)
  }

  const run = async () => {
    if (!preview) return
    setBusy(true)
    setError(null)
    try {
      await apiJson(user, `/api/god-mode/clients/${clientId}/legacy-import`, {
        method: 'POST',
        body: { extract, run_key: runKey, plan_hash: preview.plan_hash },
      })
      setDone(`Imported ${preview.to_create} records from ${fileName}.`)
      setPreview(null)
      setExtract(null)
      setFileName('')
      await loadRuns()
      onImported()
    } catch (e: any) {
      if (e instanceof ApiError && (e.body as any)?.preview) {
        setPreview((e.body as any).preview)
        setRunKey(crypto.randomUUID())
      }
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.card}>
      <h2>Import from Binwise</h2>
      <p className={styles.hint}>
        Brings the packhouse's orchards (as sites, with their blocks, sub-blocks and rows), picking jobs, contractors (as groups), teams and tags. Not people
        or picks. Make the file with <code>scripts/binwise-extract.mjs</code>; each record is imported once, so importing a later
        file adds only what is new.
      </p>
      {error && <p className={styles.error}>{error}</p>}
      {done && <p className={styles.lead}>{done}</p>}
      <div className={styles.inline}>
        <label className={styles.field}>
          Extract file
          <input
            type="file"
            accept=".json,application/json"
            disabled={busy}
            onChange={(e) => {
              choose(e.target.files?.[0])
              e.target.value = ''
            }}
          />
        </label>
        {fileName && <span className={styles.hint}>{fileName}</span>}
      </div>
      {busy && !preview && <p className={styles.lead}>Reading…</p>}

      {preview && (
        <>
          <dl className={styles.facts}>
            <div><dt>Packhouse</dt><dd>{preview.packhouse.name} ({preview.packhouse.packhouse_id})</dd></div>
            <div><dt>Extracted</dt><dd>{preview.extracted_at ? dateTimeText(preview.extracted_at) : 'Not stated'}</dd></div>
            <div><dt>To import</dt><dd>{preview.to_create}</dd></div>
            <div><dt>Held back</dt><dd>{preview.held_count}</dd></div>
          </dl>
          <div className={styles.tableCard}>
            <table className={styles.table}>
              <thead><tr><th></th><th>New</th><th>Already here</th><th>Skipped</th><th>Held back</th></tr></thead>
              <tbody>
                {SECTIONS.map(([k, label]) => (
                  <tr key={k}>
                    <td>{label}</td>
                    <td>{preview.counts[k].create}</td>
                    <td>{preview.counts[k].already}</td>
                    <td>{preview.counts[k].skipped}</td>
                    <td>{preview.counts[k].held}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.warnings.map((w, i) => (
            <p key={i} className={styles.hint}>{w}</p>
          ))}
          {preview.skipped.length > 0 && (
            <>
              <h3>Skipped</h3>
              <ul className={styles.hint}>
                {preview.skipped.map((s, i) => (
                  <li key={i}>{sectionName(s.section)}: {s.count} — {s.reason}</li>
                ))}
              </ul>
            </>
          )}
          {preview.held_count > 0 && (
            <>
              <h3>Held back</h3>
              <p className={styles.hint}>
                Not imported, each for the reason given. Fix it in Binwise and extract again, or add it by hand.
                {preview.held_count > preview.held.length && ` The first ${preview.held.length} of ${preview.held_count} are shown.`}
              </p>
              <div className={styles.tableCard}>
                <table className={styles.table}>
                  <thead><tr><th>What</th><th>Record</th><th>Why</th></tr></thead>
                  <tbody>
                    {preview.held.map((h, i) => (
                      <tr key={i}>
                        <td>{sectionName(h.section)}</td>
                        <td>{h.path}</td>
                        <td>{h.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className={styles.inline}>
            <button type="button" className={styles.primary} disabled={busy || preview.to_create === 0} onClick={run}>
              {busy ? 'Importing…' : preview.to_create === 0 ? 'Nothing new to import' : `Import ${preview.to_create} records`}
            </button>
            <button type="button" className={styles.secondary} disabled={busy} onClick={() => look(extract)}>
              Preview again
            </button>
          </div>
        </>
      )}

      <h3>Imports</h3>
      <div className={styles.tableCard}>
        {runs.length === 0 ? (
          <div className={styles.empty}>Nothing imported yet.</div>
        ) : (
          <table className={styles.table}>
            <thead><tr><th>When</th><th>By</th><th>Extracted</th><th>Imported</th><th>Held back</th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{dateTimeText(r.created_at)}</td>
                  <td>{r.created_by || '—'}</td>
                  <td>{r.extracted_at ? dateTimeText(r.extracted_at) : '—'}</td>
                  <td>
                    {SECTIONS.filter(([k]) => r.result.counts[k]?.create)
                      .map(([k, label]) => `${r.result.counts[k].create} ${label.toLowerCase()}`)
                      .join(', ')}
                  </td>
                  <td>{r.result.held_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}
